export const name = "JSON_KEYS";
export const summary = `Extracts unique JSON keys from a JSON expression.`;
export const reference = `
## JSON_KEYS

Extracts unique JSON keys from a JSON expression.



### 

\`\`\`
JSON_KEYS(
  json_expr
  [, max_depth]
  [, mode => { 'strict' | 'lax' | 'lax recursive' }]
)
\`\`\`


### Description

Extracts unique JSON keys from a JSON expression.

Arguments:

- \`json_expr\`: \`JSON\`. For example:

- \`max_depth\`: An \`INT64\` value that represents the maximum depth of nested fields to search in \`json_expr\`.

- \`mode\`: A named argument with a \`STRING\` value that can be one of the following:

Details:

- Keys are de-duplicated and returned in alphabetical order.
- Keys don't include array indices.
- Keys containing special characters are escaped using double quotes.
- Keys are case sensitive and not normalized.
- If \`json_expr\` or \`mode\` is SQL \`NULL\`, the function returns SQL \`NULL\`.
- If \`max_depth\` is SQL \`NULL\`, the function ignores the argument.
- If \`max_depth\` is less than or equal to 0, then an error is returned.


### Return type

\`ARRAY<STRING>\`


### Examples

In the following example, there are no arrays, so all keys are returned.



\`\`\`
SELECT JSON_KEYS(JSON '{"a": {"b":1}}') AS json_keys

/*-----------*
 | json_keys |
 *-----------*
 | [a, a.b] |
 *-----------*/
\`\`\`

In the following example, \`max_depth\` is set to 1 so "a.b" isn't included.



\`\`\`
SELECT JSON_KEYS(JSON '{"a": {"b":1}}', 1) AS json_keys

/*-----------*
 | json_keys |
 *-----------*
 | [a] |
 *-----------*/
\`\`\`

In the following example, \`json_expr\` contains an array. Because the mode is\`strict\`, keys inside the array are excluded.



\`\`\`
SELECT JSON_KEYS(JSON '{"a":[{"b":1}, {"c":2}], "d":3}') AS json_keys

/*-----------*
 | json_keys |
 *-----------*
 | [a, d] |
 *-----------*/
\`\`\`

In the following example, \`json_expr\` contains an array. Because the mode is\`lax\`, keys inside the array are included.



\`\`\`
SELECT JSON_KEYS(
  JSON '{"a":[{"b":1}, {"c":2}], "d":3}',
  mode => "lax") as json_keys

/*------------------*
 | json_keys |
 *------------------*
 | [a, a.b, a.c, d] |
 *------------------*/
\`\`\`

In the following example, \`json_expr\` contains consecutively nested arrays. Because the mode is \`lax\`, keys inside the consecutively nested arrays aren't included.



\`\`\`
SELECT JSON_KEYS(JSON '{"a":[[{"b":1}]]}', mode => "lax") as json_keys

/*-----------*
 | json_keys |
 *-----------*
 | [a] |
 *-----------*/
\`\`\`

In the following example, \`json_expr\` contains consecutively nested arrays. Because the mode is \`lax recursive\`, every key is returned.



\`\`\`
SELECT JSON_KEYS(JSON '{"a":[[{"b":1}]]}', mode => "lax recursive") as json_keys

/*-----------*
 | json_keys |
 *-----------*
 | [a, a.b] |
 *-----------*/
\`\`\`

In the following example, \`json_expr\` contains multiple arrays. Because the arrays aren't consecutively nested and the mode is \`lax\`, keys inside the arrays are included.



\`\`\`
SELECT JSON_KEYS(JSON '{"a":[{"b":[{"c":1}]}]}', mode => "lax") as json_keys

/*-----------------*
 | json_keys |
 *-----------------*
 | [a, a.b, a.b.c] |
 *-----------------*/
\`\`\`

In the following example, \`json_expr\` contains both consecutively nested and single arrays. Because the mode is \`lax\`, keys inside the consecutively nested arrays are excluded.



\`\`\`
SELECT JSON_KEYS(JSON '{"a":[{"b":[[{"c":1}]]}]}', mode => "lax") as json_keys

/*-----------*
 | json_keys |
 *-----------*
 | [a, a.b] |
 *-----------*/
\`\`\`

In the following example, \`json_expr\` contains both consecutively nested and single arrays. Because the mode is \`lax recursive\`, all keys are included.



\`\`\`
SELECT JSON_KEYS(
  JSON '{"a":[{"b":[[{"c":1}]]}]}', mode => "lax recursive") as json_keys

/*-----------------*
 | json_keys |
 *-----------------*
 | [a, a.b, a.b.c] |
 *-----------------*/
\`\`\`


source: https://cloud.google.com/bigquery/docs/reference/standard-sql/json_functions
`;
